// src/pages/EditProfilePage.js

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { doc, getDoc, updateDoc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { FaSave, FaTimes, FaTrash, FaEye, FaEyeSlash, FaCalendarAlt, FaShieldAlt, FaPhone, FaEnvelope, FaPaperPlane, FaGlobe, FaCar, FaComments } from 'react-icons/fa';
import { resolveContactField, formatPhoneInput } from '../lib/phoneUtils';
import QrDirectChat from '../components/QrDirectChat';

/**
 * Top-level standalone input component.
 * CRITICAL FIX: Hoisted OUTSIDE EditProfilePage to prevent unmounting/remounting on every keystroke in mobile view.
 */
const ContactInputField = ({
    field,
    icon: Icon,
    label,
    placeholder,
    type = 'text',
    value,
    isVisible,
    onChange,
    onToggle,
    disabled
}) => {
    return (
        <div className={`p-4 rounded-2xl border transition-all duration-200 ${
            isVisible 
                ? 'bg-white border-gray-200 shadow-sm' 
                : 'bg-gray-50/70 border-dashed border-gray-300'
        }`}>
            <div className="flex items-center justify-between mb-2">
                <label 
                    htmlFor={`contactInfo.${field}.value`} 
                    className="flex items-center gap-2 text-xs font-semibold text-gray-700 select-none cursor-pointer"
                >
                    <Icon className={isVisible ? "text-blue-600" : "text-gray-400"} size={14} />
                    <span>{label}</span>
                </label>

                {/* Apple-style visibility switch pill */}
                <button
                    type="button"
                    onClick={() => onToggle(field, !isVisible)}
                    disabled={disabled}
                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-all ${
                        isVisible
                            ? 'bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200/60'
                            : 'bg-gray-200/80 text-gray-600 hover:bg-gray-300 border border-gray-300/60'
                    }`}
                    title={isVisible ? "Bu bilgi QR taranınca görünür" : "Bu bilgi gizli tutulur, gösterilmez"}
                >
                    {isVisible ? (
                        <>
                            <FaEye size={12} className="text-blue-600" />
                            <span>Görünür</span>
                        </>
                    ) : (
                        <>
                            <FaEyeSlash size={12} className="text-gray-500" />
                            <span>Gizli</span>
                        </>
                    )}
                </button>
            </div>

            <div className="relative">
                <input
                    type={type}
                    id={`contactInfo.${field}.value`}
                    name={`contactInfo.${field}.value`}
                    value={value || ''}
                    onChange={(e) => {
                        const val = field === 'phone' ? formatPhoneInput(e.target.value) : e.target.value;
                        onChange(field, val);
                    }}
                    placeholder={placeholder}
                    disabled={disabled}
                    className={`w-full px-3.5 py-2.5 rounded-xl text-sm transition-all outline-none border ${
                        isVisible
                            ? 'border-gray-200 bg-white text-gray-900 focus:border-blue-500 focus:ring-2 focus:ring-blue-100'
                            : 'border-gray-200 bg-gray-100/70 text-gray-700 focus:border-gray-400 focus:bg-white'
                    }`}
                />
            </div>
            {!isVisible && (
                <p className="text-[11px] text-gray-400 mt-1 pl-1">
                    Bu alan gizlenmiştir. QR okutulduğunda ziyaretçilere gösterilmez.
                </p>
            )}
        </div>
    );
};

const EditProfilePage = () => {
    const { profileId } = useParams();
    const { currentUser } = useAuth();
    const navigate = useNavigate();

    const [profileData, setProfileData] = useState(null);
    const [formData, setFormData] = useState({
        vehiclePlate: '',
        visibleNote: '',
        allowDirectChat: true,
        contactInfo: {
            phone: { value: '', isVisible: true },
            email: { value: '', isVisible: true },
            telegram: { value: '', isVisible: true },
            website: { value: '', isVisible: true },
        },
        internalData: {
            muayeneGecerlilikTarihi: '',
            sigortaExpireDate: '',
            kaskoExpireDate: '',
        }
    });

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState(false);

    const fetchProfile = useCallback(async () => {
        if (!currentUser || !profileId) return;
        setLoading(true);
        setError('');
        try {
            const profileDocRef = doc(db, 'vehicleProfiles', profileId);
            const docSnap = await getDoc(profileDocRef);

            if (docSnap.exists()) {
                const data = docSnap.data();
                if (data.userId === currentUser.uid) {
                    setProfileData(data);
                    const ci = data.contactInfo || {};
                    const idata = data.internalData || {};

                    // Robust resolution of contact fields (handling string vs object)
                    const resolvedPhone = resolveContactField(ci.phone);
                    const resolvedEmail = resolveContactField(ci.email);
                    const resolvedTelegram = resolveContactField(ci.telegram);
                    const resolvedWebsite = resolveContactField(ci.website);

                    setFormData({
                        vehiclePlate: data.vehiclePlate || '',
                        visibleNote: data.visibleNote || '',
                        allowDirectChat: data.allowDirectChat !== false,
                        contactInfo: {
                            phone: resolvedPhone,
                            email: resolvedEmail,
                            telegram: resolvedTelegram,
                            website: resolvedWebsite,
                        },
                        internalData: {
                            muayeneGecerlilikTarihi: idata.muayeneGecerlilikTarihi || '',
                            sigortaExpireDate: idata.sigortaExpireDate || '',
                            kaskoExpireDate: idata.kaskoExpireDate || '',
                        }
                    });
                } else {
                    setError('Bu profili düzenleme yetkiniz yok.');
                    navigate('/dashboard');
                }
            } else {
                setError('Profil bulunamadı.');
                navigate('/dashboard');
            }
        } catch (err) {
            console.error("Error fetching profile: ", err);
            setError('Profil yüklenirken bir hata oluştu.');
        } finally {
            setLoading(false);
        }
    }, [currentUser, profileId, navigate]);

    useEffect(() => {
        fetchProfile();
    }, [fetchProfile]);

    const handleContactChange = useCallback((field, value) => {
        setFormData(prev => ({
            ...prev,
            contactInfo: {
                ...prev.contactInfo,
                [field]: {
                    ...prev.contactInfo[field],
                    value
                }
            }
        }));
    }, []);

    const handleContactToggle = useCallback((field, isVisible) => {
        setFormData(prev => ({
            ...prev,
            contactInfo: {
                ...prev.contactInfo,
                [field]: {
                    ...prev.contactInfo[field],
                    isVisible
                }
            }
        }));
    }, []);

    const handleGeneralChange = (e) => {
        const { name, value } = e.target;
        if (name.startsWith('internalData.')) {
            const [, field] = name.split('.');
            setFormData(prev => ({
                ...prev,
                internalData: {
                    ...prev.internalData,
                    [field]: value
                }
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!currentUser || !profileId) return;
        setSaving(true);
        setError('');

        try {
            const profileDocRef = doc(db, 'vehicleProfiles', profileId);

            // Clean, normalized data model for Firestore
            const cleanData = {
                vehiclePlate: formData.vehiclePlate.trim().toUpperCase(),
                visibleNote: formData.visibleNote.trim(),
                allowDirectChat: formData.allowDirectChat !== false,
                contactInfo: {
                    phone: {
                        value: formData.contactInfo.phone.value.trim(),
                        isVisible: !!formData.contactInfo.phone.isVisible
                    },
                    email: {
                        value: formData.contactInfo.email.value.trim(),
                        isVisible: !!formData.contactInfo.email.isVisible
                    },
                    telegram: {
                        value: formData.contactInfo.telegram.value.trim(),
                        isVisible: !!formData.contactInfo.telegram.isVisible
                    },
                    website: {
                        value: formData.contactInfo.website.value.trim(),
                        isVisible: !!formData.contactInfo.website.isVisible
                    }
                },
                internalData: {
                    muayeneGecerlilikTarihi: formData.internalData.muayeneGecerlilikTarihi || '',
                    sigortaExpireDate: formData.internalData.sigortaExpireDate || '',
                    kaskoExpireDate: formData.internalData.kaskoExpireDate || '',
                },
                updatedAt: serverTimestamp()
            };

            await updateDoc(profileDocRef, cleanData);
            setSaving(false);
            navigate('/dashboard');
        } catch (err) {
            console.error("Error updating profile: ", err);
            setError('Profil güncellenirken bir hata oluştu. Lütfen tekrar deneyin.');
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!currentUser || !profileId || !deleteConfirm) return;
        setSaving(true);
        setError('');

        const profileDocRef = doc(db, 'vehicleProfiles', profileId);
        const qrDocRef = doc(db, 'qrCodes', profileId);

        try {
            const batch = writeBatch(db);
            batch.delete(profileDocRef);
            batch.update(qrDocRef, {
                isLinked: false,
                linkedUserId: null,
                linkedAt: null
            });
            await batch.commit();
            setSaving(false);
            alert('Numaratör bağlantısı başarıyla kaldırıldı.');
            navigate('/dashboard');
        } catch (err) {
            console.error("Error deleting profile link: ", err);
            setError('Numaratör bağlantısı kaldırılırken hata oluştu.');
            setSaving(false);
            setDeleteConfirm(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <div className="flex flex-col items-center gap-3 text-gray-500">
                    <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-sm font-medium">Profil bilgileri yükleniyor...</span>
                </div>
            </div>
        );
    }

    if (error && !profileData) {
        return (
            <div className="max-w-md mx-auto px-4 py-16 text-center">
                <div className="bg-red-50 border border-red-200 text-red-700 p-6 rounded-2xl">
                    <p className="font-semibold">{error}</p>
                    <Link to="/dashboard" className="inline-block mt-4 text-xs font-bold text-red-800 underline">
                        Panele Geri Dön
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F5F5F7] py-8 sm:py-12 px-4 sm:px-6">
            <div className="max-w-2xl mx-auto">
                <div className="bg-white/80 backdrop-blur-xl border border-black/[0.06] shadow-[0_8px_30px_rgb(0,0,0,0.06)] rounded-3xl p-6 sm:p-10 relative">
                    
                    {/* Header */}
                    <div className="flex items-center justify-between pb-6 border-b border-gray-100 mb-6">
                        <div>
                            <div className="flex items-center gap-2">
                                <span className="px-2.5 py-0.5 rounded-full text-[11px] font-mono font-bold bg-gray-100 text-gray-700">
                                    {profileId}
                                </span>
                                <span className="text-xs text-gray-400">Aktif Numaratör</span>
                            </div>
                            <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight mt-1">
                                Numaratör Bilgilerini Düzenle
                            </h1>
                        </div>
                        <Link 
                            to="/dashboard" 
                            className="w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 flex items-center justify-center transition-colors"
                            aria-label="Kapat"
                        >
                            <FaTimes size={16} />
                        </Link>
                    </div>

                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6 text-sm">
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSave} className="space-y-6">
                        
                        {/* Vehicle Plate Card */}
                        <div>
                            <label htmlFor="vehiclePlate" className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                <FaCar className="text-gray-500" />
                                <span>Araç Plakası</span>
                            </label>
                            <input
                                type="text"
                                id="vehiclePlate"
                                name="vehiclePlate"
                                value={formData.vehiclePlate}
                                onChange={handleGeneralChange}
                                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl font-mono font-bold uppercase text-gray-900 tracking-wider text-base focus:border-black focus:ring-2 focus:ring-black/5 outline-none disabled:bg-gray-100 disabled:text-gray-500"
                                placeholder="Örn: 35 AZ 945"
                                disabled={saving || Boolean(profileData && profileData.vehiclePlate)}
                            />
                            {profileData && profileData.vehiclePlate && (
                                <p className="text-xs text-gray-400 mt-1.5 flex items-center gap-1">
                                    <FaShieldAlt size={11} className="text-gray-400" />
                                    <span>Güvenlik nedeniyle atanmış araç plakası değiştirilemez.</span>
                                </p>
                            )}
                        </div>

                        {/* Visible Note */}
                        <div>
                            <div className="flex justify-between items-center mb-2">
                                <label htmlFor="visibleNote" className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                                    Görünür Mesaj / Not
                                </label>
                                <span className="text-xs font-mono text-gray-400">
                                    {formData.visibleNote.length} / 150
                                </span>
                            </div>
                            <textarea
                                id="visibleNote"
                                name="visibleNote"
                                rows={3}
                                value={formData.visibleNote}
                                onChange={handleGeneralChange}
                                maxLength={150}
                                disabled={saving}
                                placeholder="QR kodu tarayanların göreceği mesaj (örn: 10 dakika sonra aracımın başındayım)..."
                                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm text-gray-900 focus:border-black focus:ring-2 focus:ring-black/5 outline-none resize-none"
                            />
                        </div>

                        {/* Contact Channels Section with Individual Toggles */}
                        <div className="pt-4 border-t border-gray-100">
                            <div className="mb-4">
                                <h2 className="text-base font-bold text-gray-900 tracking-tight">
                                    İletişim Kanalları & Gizlilik Ayarları
                                </h2>
                                <p className="text-xs text-gray-500 mt-0.5">
                                    Her kanalın yanındaki butonla görünürlüğünü açabilir veya gizleyebilirsiniz. Gizli kanallar QR taramasında asla gösterilmez.
                                </p>
                            </div>

                            <div className="space-y-3">
                                <ContactInputField
                                    field="phone"
                                    icon={FaPhone}
                                    label="Telefon Numarası"
                                    placeholder="0 (5XX) XXX XX XX"
                                    type="tel"
                                    value={formData.contactInfo.phone.value}
                                    isVisible={formData.contactInfo.phone.isVisible}
                                    onChange={handleContactChange}
                                    onToggle={handleContactToggle}
                                    disabled={saving}
                                />

                                <ContactInputField
                                    field="email"
                                    icon={FaEnvelope}
                                    label="E-Posta Adresi"
                                    placeholder="ornek@alanadi.com"
                                    type="email"
                                    value={formData.contactInfo.email.value}
                                    isVisible={formData.contactInfo.email.isVisible}
                                    onChange={handleContactChange}
                                    onToggle={handleContactToggle}
                                    disabled={saving}
                                />

                                <ContactInputField
                                    field="telegram"
                                    icon={FaPaperPlane}
                                    label="Telegram Kullanıcı Adı"
                                    placeholder="@kullaniciadi"
                                    type="text"
                                    value={formData.contactInfo.telegram.value}
                                    isVisible={formData.contactInfo.telegram.isVisible}
                                    onChange={handleContactChange}
                                    onToggle={handleContactToggle}
                                    disabled={saving}
                                />

                                <ContactInputField
                                    field="website"
                                    icon={FaGlobe}
                                    label="Web Sitesi / İlan Linki"
                                    placeholder="https://..."
                                    type="url"
                                    value={formData.contactInfo.website.value}
                                    isVisible={formData.contactInfo.website.isVisible}
                                    onChange={handleContactChange}
                                    onToggle={handleContactToggle}
                                    disabled={saving}
                                />
                            </div>
                        </div>

                        {/* Maintenance Dates (Private, only visible to vehicle owner) */}
                        <div className="pt-4 border-t border-gray-100">
                            <div className="flex items-center gap-2 mb-2">
                                <FaCalendarAlt className="text-blue-600" />
                                <h2 className="text-base font-bold text-gray-900 tracking-tight">
                                    Araç Takip & Muayene Bilgileri
                                </h2>
                            </div>
                            <p className="text-xs text-gray-500 mb-4">
                                Bu tarihler yalnızca sizin takip etmeniz içindir. QR kodu tarayan üçüncü kişilere kesinlikle gösterilmez.
                            </p>

                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                <div className="p-3 rounded-2xl bg-gray-50 border border-gray-200">
                                    <label htmlFor="internalData.muayeneGecerlilikTarihi" className="block text-[11px] font-bold text-gray-600 uppercase mb-1">
                                        Muayene Tarihi
                                    </label>
                                    <input
                                        type="date"
                                        id="internalData.muayeneGecerlilikTarihi"
                                        name="internalData.muayeneGecerlilikTarihi"
                                        value={formData.internalData.muayeneGecerlilikTarihi}
                                        onChange={handleGeneralChange}
                                        disabled={saving}
                                        className="w-full bg-white px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-800 outline-none"
                                    />
                                </div>

                                <div className="p-3 rounded-2xl bg-gray-50 border border-gray-200">
                                    <label htmlFor="internalData.sigortaExpireDate" className="block text-[11px] font-bold text-gray-600 uppercase mb-1">
                                        Sigorta Bitiş
                                    </label>
                                    <input
                                        type="date"
                                        id="internalData.sigortaExpireDate"
                                        name="internalData.sigortaExpireDate"
                                        value={formData.internalData.sigortaExpireDate}
                                        onChange={handleGeneralChange}
                                        disabled={saving}
                                        className="w-full bg-white px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-800 outline-none"
                                    />
                                </div>

                                <div className="p-3 rounded-2xl bg-gray-50 border border-gray-200">
                                    <label htmlFor="internalData.kaskoExpireDate" className="block text-[11px] font-bold text-gray-600 uppercase mb-1">
                                        Kasko Bitiş
                                    </label>
                                    <input
                                        type="date"
                                        id="internalData.kaskoExpireDate"
                                        name="internalData.kaskoExpireDate"
                                        value={formData.internalData.kaskoExpireDate}
                                        onChange={handleGeneralChange}
                                        disabled={saving}
                                        className="w-full bg-white px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-800 outline-none"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Direct QR Chat Control Setting */}
                        <div className="pt-4 border-t border-gray-100">
                            <div className="p-4 rounded-2xl bg-gray-50 border border-gray-200 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center text-lg">
                                        <FaComments />
                                    </div>
                                    <div>
                                        <h3 className="text-sm font-bold text-gray-900">
                                            Karekod Doğrudan Mesajlaşma (Canlı Sohbet)
                                        </h3>
                                        <p className="text-xs text-gray-500 mt-0.5">
                                            Karekodunuzu taratan kişilerin sayfa üzerinden isim/rumuz girerek sizinle doğrudan mesajlaşmasına izin verin.
                                        </p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setFormData(prev => ({ ...prev, allowDirectChat: !prev.allowDirectChat }))}
                                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                                        formData.allowDirectChat ? 'bg-black' : 'bg-gray-300'
                                    }`}
                                >
                                    <span
                                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-md ring-0 transition duration-200 ease-in-out ${
                                            formData.allowDirectChat ? 'translate-x-5' : 'translate-x-0'
                                        }`}
                                    />
                                </button>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="pt-6 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4">
                            <div>
                                {!deleteConfirm ? (
                                    <button 
                                        type="button" 
                                        onClick={() => setDeleteConfirm(true)} 
                                        className="flex items-center gap-1.5 text-xs font-semibold text-red-600 hover:text-red-700 transition-colors"
                                        disabled={saving}
                                    >
                                        <FaTrash size={12} />
                                        <span>Numaratör Bağlantısını Kaldır</span>
                                    </button>
                                ) : (
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs text-red-700 font-bold">Emin misiniz?</span>
                                        <button 
                                            type="button" 
                                            onClick={handleDelete} 
                                            className="px-3 py-1 bg-red-600 text-white text-xs font-semibold rounded-lg hover:bg-red-700 disabled:opacity-50"
                                            disabled={saving}
                                        >
                                            Evet, Kaldır
                                        </button>
                                        <button 
                                            type="button" 
                                            onClick={() => setDeleteConfirm(false)} 
                                            className="px-3 py-1 bg-gray-200 text-gray-700 text-xs font-semibold rounded-lg hover:bg-gray-300"
                                            disabled={saving}
                                        >
                                            İptal
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div className="flex items-center gap-3 w-full sm:w-auto">
                                <Link 
                                    to="/dashboard" 
                                    className="flex-1 sm:flex-none text-center px-5 py-2.5 rounded-xl border border-gray-200 text-gray-700 text-xs font-semibold hover:bg-gray-50 transition-colors"
                                >
                                    Vazgeç
                                </Link>
                                <button 
                                    type="submit" 
                                    disabled={saving} 
                                    className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-6 py-2.5 bg-black text-white text-xs font-bold rounded-xl hover:bg-gray-900 disabled:opacity-50 transition-all shadow-sm"
                                >
                                    <FaSave size={13} />
                                    <span>{saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}</span>
                                </button>
                            </div>
                        </div>

                    </form>

                    {/* Owner Live Chat Viewer & Reply Interface */}
                    <div className="mt-8 pt-6 border-t border-gray-100">
                        <div className="mb-3">
                            <h3 className="text-sm font-bold text-gray-900">
                                Gelen Karekod Mesajları
                            </h3>
                            <p className="text-xs text-gray-500">
                                Aracınızın QR kodunu okutan kişilerin gönderdiği mesajları buradan görüntüleyebilir ve araç sahibi olarak anında yanıtlayabilirsiniz.
                            </p>
                        </div>
                        <QrDirectChat qrCodeId={profileId} profileData={formData} isOwner={true} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditProfilePage;